import styled from 'styled-components';
import { useLayoutEffect, useState } from 'react';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { Trans } from 'react-i18next';

import { Button } from 'shared/components/Button';
import {
  AffirmPromotional,
  AffirmPromotionalClass,
  AffirmPromotionalPageType,
  AffirmPromotionalType,
} from 'shared/components/AffirmPromotional';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { FeatureFlags } from 'featureFlags/constants';
import { LOCALES } from 'localization/constants/configEnums';
import { GiftCard } from 'checkout/components/Payment/GiftCard';
import { t } from 'localization/utils/t';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';

const TP = 'checkout.components.Payment.Affirm';

export const Affirm = () => {
  const { publicRuntimeConfig } = getConfig();
  const router = useRouter();

  const { order, addresses } = useCheckoutContext();

  const [isAffirmLoading, setIsAffirmLoading] = useState<boolean>(true);

  const isGuestCheckoutEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_GUEST_CHECKOUT);

  const shippingAddress = addresses[order.addressId];

  const { orderNumber } = router.query;
  const baseUrl =
    router.locale === LOCALES.enUS
      ? `${window.location.origin}/checkout`
      : `${window.location.origin}/${router.locale}/checkout`;

  const handleAffirmClick = () => {
    // @ts-ignore global var
    window.affirm.checkout({
      config: {
        financial_product_key: publicRuntimeConfig.AFFIRM_FINANCIAL_KEY,
      },
      merchant: {
        user_cancel_url: isGuestCheckoutEnabled
          ? `${baseUrl}?orderNumber=${orderNumber.toString()}&status=CANCELLED`
          : `${baseUrl}/${orderNumber.toString()}?status=CANCELLED`,
        user_confirmation_url: isGuestCheckoutEnabled
          ? `${baseUrl}?orderNumber=${orderNumber.toString()}`
          : `${baseUrl}/${orderNumber.toString()}`,
        user_confirmation_url_action: 'GET',
      },
      shipping: {
        name: {
          full: shippingAddress.name,
        },
        address: {
          line1: shippingAddress.address1,
          line2: shippingAddress.address2 || '',
          city: shippingAddress.city,
          state: shippingAddress.state,
          zipcode: shippingAddress.postalCode,
        },
      },
      items: [
        {
          display_name: order.product.productTemplate.name,
          sku: order.product.productTemplate.sku,
          unit_price: order.product.localizedPriceCents?.amountUsdCents,
          qty: 1,
          item_image_url: order.product.productTemplate.pictureUrl,
          item_url: `${window.location.origin}/sneakers/${order.product.productTemplate.slug}`,
        },
      ],
      currency: 'USD',
      tax_amount: order?.localizedTaxCents?.amountUsdCents,
      shipping_amount: order?.localizedShippingCents?.amountUsdCents,
      total: order?.localizedFinalPriceCents?.amountUsdCents,
    });
    // @ts-ignore global var
    window.affirm.checkout.open();
    setIsAffirmLoading(true);
  };

  useLayoutEffect(() => {
    // @ts-ignore global var
    window.affirm?.ui?.ready(() => {
      setIsAffirmLoading(false);
    });
  }, []);

  return (
    <>
      <GiftCard />
      <AffirmPromotional
        affirmClass={AffirmPromotionalClass.AsLowAs}
        pageType={AffirmPromotionalPageType.Payment}
        affirmType={AffirmPromotionalType.Text}
        amount={order?.localizedFinalPriceCents?.amountUsdCents}
      />

      <Affirm.Wrapper data-qa="affirm_redirect_instructions">
        <p>
          {t(
            `${TP}.affirmInstructions`,
            'You will be redirected to Affirm to complete your purchase securely.',
          )}
        </p>
        <p>
          <Trans
            i18nKey={`${TP}.affirmTerms`}
            defaults="By proceeding, you confirm you have read and agree to the <br> <purchasePolicyLink>Purchases and Returns Policy</purchasePolicyLink>."
            components={{
              purchasePolicyLink: (
                <Affirm.Link
                  href={getRedirectionPath('/returns')}
                  target="_blank"
                  rel="noreferrer"
                />
              ),
            }}
          />
        </p>
        <Button
          data-qa="checkout_continue_affirm"
          disabled={isAffirmLoading}
          loading={isAffirmLoading}
          onClick={handleAffirmClick}
        >
          {t(`${TP}.continueAffirmButton`, 'Place Order with Affirm')}
        </Button>
      </Affirm.Wrapper>
    </>
  );
};

Affirm.Wrapper = styled.div`
  width: 100%;

  p {
    margin-bottom: 16px;
  }
  button {
    margin-top: 48px;
  }
`;

Affirm.Link = styled.a`
  text-decoration: underline;
`;

Affirm.Image = styled.img`
  width: 82px;
  margin-bottom: 12px;
`;
